import { addDays, parseISODate, weekDays } from './dates';
import { localDate } from './schedule';

// The coach's week as a picture to save, print or send ("Chia sẻ lịch tuần"), drawn on a canvas in the app.
// Landscape is an A4 grid (days × hours); portrait is a phone-sized list by day. `mode`: "all" (the coach's week),
// "person" (one student's sessions, to send them) or "free" (when the coach is still free: sessions without
// names). Nothing leaves the phone until the coach shares the file.

const FONT = '"Be Vietnam Pro", system-ui, -apple-system, sans-serif';
const C = {
  ink: '#17201b', muted: '#6b766f', line: '#e3e7e4', faint: '#f4f6f5', green: '#17804f', greenText: '#ffffff',
  dashed: '#9aa59f', absentFill: '#fdebe4', absentLine: '#e8743b', absentText: '#8e3414', busy: '#e8ebe9',
};
const DAY_NAMES = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ nhật'];
const SHORT = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const LANDSCAPE = { width: 1754, height: 1240 }; // A4 at 150 dpi
const PORTRAIT_WIDTH = 1080;

const dm = (iso) => { const d = parseISODate(iso); return `${d.getDate()}/${d.getMonth() + 1}`; };
const hhmm = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const font = (size, weight = 400) => `${weight} ${size}px ${FONT}`;

// The people in a week's sessions, for "1 học viên": { key, name, offline }.
export function peopleOf(appointments) {
  const seen = new Map();
  appointments.forEach((a) => {
    const key = a.offline ? `o${a.offline.id}` : `s${a.client_id}`;
    if (!seen.has(key)) seen.set(key, { key, name: a.client_name, offline: !!a.offline });
  });
  return [...seen.values()].sort((x, y) => x.name.localeCompare(y.name, 'vi'));
}

const keyOf = (a) => (a.offline ? `o${a.offline.id}` : `s${a.client_id}`);

// The sessions as blocks: day index, minutes from midnight, who and how to draw them.
function blocks(week, appointments, { mode, personKey, hideNames }) {
  const days = weekDays(week);
  return appointments
    .filter((a) => mode !== 'person' || keyOf(a) === personKey)
    .map((a) => {
      const start = new Date(a.starts_at);
      const day = days.indexOf(localDate(a.starts_at));
      const from = start.getHours() * 60 + start.getMinutes();
      const kind = mode === 'free' ? 'busy' : a.state === 'absent' ? 'absent' : a.offline ? 'offline' : 'app';
      const name = mode === 'free' ? 'Đã có lịch' : hideNames ? 'Học viên' : a.client_name;
      return { day, from, to: from + a.duration_min, kind, name, location: a.location, note: mode === 'free' ? null : a.note };
    })
    .filter((b) => b.day >= 0)
    .sort((x, y) => x.day - y.day || x.from - y.from);
}

// "16 buổi · 17,5 giờ · Gym Tân Bình" (the place most sessions are at).
function summary(items) {
  const hours = items.reduce((sum, b) => sum + (b.to - b.from), 0) / 60;
  const places = {};
  items.forEach((b) => { if (b.location) places[b.location] = (places[b.location] || 0) + 1; });
  const place = Object.entries(places).sort((x, y) => y[1] - x[1])[0]?.[0];
  return [`${items.length} buổi`, `${String(Math.round(hours * 10) / 10).replace('.', ',')} giờ`, place].filter(Boolean).join(' · ');
}

function title(week, { mode, personName, coachName }) {
  const range = `${dm(week)} – ${dm(addDays(week, 6))}`;
  if (mode === 'free') return `Lịch trống tuần ${range} · Coach ${coachName}`;
  if (mode === 'person') return `Lịch tập tuần ${range} · ${personName} với Coach ${coachName}`;
  return `Lịch tập tuần ${range} · Coach ${coachName}`;
}

function fit(ctx, text, max) {
  if (ctx.measureText(text).width <= max) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > max) out = out.slice(0, -1);
  return `${out}…`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

// One block's box: filled green (app student), dashed (no app), orange stripes (reported absent), grey (free mode).
function drawBox(ctx, x, y, w, h, kind) {
  ctx.save();
  roundRect(ctx, x, y, w, h, 10);
  if (kind === 'app') {
    ctx.fillStyle = C.green;
    ctx.fill();
  } else if (kind === 'busy') {
    ctx.fillStyle = C.busy;
    ctx.fill();
  } else if (kind === 'absent') {
    ctx.fillStyle = C.absentFill;
    ctx.fill();
    ctx.clip();
    ctx.strokeStyle = 'rgba(232,116,59,0.25)';
    ctx.lineWidth = 4;
    for (let i = -h; i < w; i += 14) { ctx.beginPath(); ctx.moveTo(x + i, y + h); ctx.lineTo(x + i + h, y); ctx.stroke(); }
    ctx.restore();
    ctx.save();
    roundRect(ctx, x, y, w, h, 10);
    ctx.strokeStyle = C.absentLine;
    ctx.lineWidth = 2;
    ctx.stroke();
  } else {
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.setLineDash([7, 5]);
    ctx.strokeStyle = C.dashed;
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  ctx.restore();
}

const textColor = (kind) => ({ app: C.greenText, absent: C.absentText, busy: C.muted }[kind] || C.ink);

function legend(ctx, x, y, mode, size = 18) {
  const items = mode === 'free' ? [['busy', 'Đã có lịch'], ['empty', 'Trống']]
    : [['app', 'Học viên dùng app'], ['offline', 'Chưa dùng app'], ['absent', 'Báo nghỉ'], ['empty', 'Trống']];
  ctx.font = font(size);
  let cx = x;
  items.forEach(([kind, label]) => {
    if (kind === 'empty') {
      ctx.strokeStyle = C.line;
      ctx.lineWidth = 2;
      roundRect(ctx, cx, y - 14, 26, 18, 5);
      ctx.stroke();
    } else drawBox(ctx, cx, y - size * 0.8, size * 1.4, size, kind);
    ctx.fillStyle = C.ink;
    ctx.fillText(label, cx + size * 1.9, y);
    cx += size * 1.9 + ctx.measureText(label).width + size * 1.5;
  });
}

function landscape(canvas, week, items, opts) {
  const { width, height } = LANDSCAPE;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  const m = 56;

  ctx.fillStyle = C.ink;
  ctx.font = font(34, 800);
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(fit(ctx, title(week, opts), width - 2 * m - 420), m, m + 30);
  ctx.font = font(20);
  ctx.fillStyle = C.muted;
  const stats = opts.mode === 'free' ? 'Ô trắng là giờ còn trống' : summary(items);
  ctx.textAlign = 'right';
  ctx.fillText(stats, width - m, m + 28);
  ctx.textAlign = 'left';

  // Hours shown: 06:00–22:00, widened to fit the earliest and latest session.
  const startHour = Math.min(6, ...items.map((b) => Math.floor(b.from / 60)));
  const endHour = Math.max(22, ...items.map((b) => Math.ceil(b.to / 60)));
  const top = m + 110;
  const bottom = height - m - 50;
  const left = m + 90;
  const colW = (width - m - left) / 7;
  const hourH = (bottom - top) / (endHour - startHour);
  const days = weekDays(week);

  ctx.font = font(20, 700);
  ctx.textAlign = 'center';
  days.forEach((d, i) => {
    ctx.fillStyle = C.ink;
    ctx.fillText(`${SHORT[i]} ${dm(d)}`, left + colW * i + colW / 2, top - 22);
  });
  ctx.textAlign = 'left';

  ctx.lineWidth = 1;
  for (let h = startHour; h <= endHour; h += 1) {
    const y = top + (h - startHour) * hourH;
    ctx.strokeStyle = C.line;
    ctx.beginPath(); ctx.moveTo(left, y); ctx.lineTo(width - m, y); ctx.stroke();
    if (h < endHour) {
      ctx.fillStyle = C.muted;
      ctx.font = font(17);
      ctx.fillText(hhmm(h * 60), m, y + 18);
    }
  }
  for (let i = 0; i <= 7; i += 1) {
    const x = left + colW * i;
    ctx.strokeStyle = C.line;
    ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, bottom); ctx.stroke();
  }

  items.forEach((b) => {
    const x = left + colW * b.day + 6;
    const w = colW - 12;
    const y = top + ((b.from - startHour * 60) / 60) * hourH + 2;
    const h = Math.max(((b.to - b.from) / 60) * hourH - 4, 34);
    drawBox(ctx, x, y, w, h, b.kind);
    ctx.fillStyle = textColor(b.kind);
    ctx.font = font(18, 700);
    const name = b.kind === 'absent' ? `${b.name} · nghỉ` : b.name;
    ctx.fillText(fit(ctx, name, w - 16), x + 8, y + 22);
    if (h > 44) {
      ctx.font = font(15);
      ctx.fillText(hhmm(b.from), x + 8, y + 40);
    }
  });

  legend(ctx, m, height - m + 4, opts.mode);
  ctx.font = font(16);
  ctx.fillStyle = C.muted;
  ctx.textAlign = 'right';
  ctx.fillText('Tạo bằng Macro Mate', width - m, height - m + 4);
  ctx.textAlign = 'left';
}

// Free windows of a day between 06:00 and 22:00, for the portrait "Lịch trống".
function freeWindows(dayItems) {
  const out = [];
  let at = 6 * 60;
  dayItems.forEach((b) => {
    if (b.from - at >= 30) out.push([at, b.from]);
    at = Math.max(at, b.to);
  });
  if (22 * 60 - at >= 30) out.push([at, 22 * 60]);
  return out;
}

function portrait(canvas, week, items, opts) {
  const width = PORTRAIT_WIDTH;
  const m = 56;
  const rowH = 92;
  const days = weekDays(week);
  const byDay = days.map((_, i) => items.filter((b) => b.day === i));
  const rows = byDay.map((list) => (opts.mode === 'free' ? Math.max(freeWindows(list).length, 1) : Math.max(list.length, 1)));
  const height = Math.max(1920, 200 + rows.reduce((sum, n) => sum + 70 + n * rowH, 0) + 120);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = C.ink;
  ctx.font = font(40, 800);
  const words = title(week, opts).split(' · ');
  ctx.fillText(fit(ctx, words[0], width - 2 * m), m, m + 40);
  ctx.font = font(28);
  ctx.fillStyle = C.muted;
  ctx.fillText(fit(ctx, words.slice(1).join(' · '), width - 2 * m), m, m + 84);
  if (opts.mode !== 'free') ctx.fillText(fit(ctx, summary(items), width - 2 * m), m, m + 124);

  let y = m + 180;
  days.forEach((d, i) => {
    ctx.fillStyle = C.ink;
    ctx.font = font(30, 800);
    ctx.fillText(`${DAY_NAMES[i]} ${dm(d)}`, m, y);
    y += 22;
    const list = byDay[i];
    if (opts.mode === 'free') {
      const windows = freeWindows(list);
      if (windows.length === 0) {
        ctx.font = font(26); ctx.fillStyle = C.muted; ctx.fillText('Kín lịch', m, y + 44); y += rowH;
      }
      windows.forEach(([from, to]) => {
        ctx.save(); ctx.strokeStyle = C.green; ctx.lineWidth = 2; roundRect(ctx, m, y + 8, width - 2 * m, rowH - 18, 14); ctx.stroke(); ctx.restore();
        ctx.fillStyle = C.green; ctx.font = font(30, 700); ctx.fillText(`${hhmm(from)} – ${hhmm(to)}`, m + 24, y + 56);
        ctx.font = font(24); ctx.fillStyle = C.muted; ctx.textAlign = 'right'; ctx.fillText('Còn trống', width - m - 24, y + 56); ctx.textAlign = 'left';
        y += rowH;
      });
    } else if (list.length === 0) {
      ctx.font = font(26); ctx.fillStyle = C.muted; ctx.fillText('Không có buổi tập', m, y + 44); y += rowH;
    } else {
      list.forEach((b) => {
        drawBox(ctx, m, y + 8, width - 2 * m, rowH - 18, b.kind);
        ctx.fillStyle = textColor(b.kind);
        ctx.font = font(28, 700);
        ctx.fillText(`${hhmm(b.from)} – ${hhmm(b.to)}`, m + 24, y + 56);
        const name = b.kind === 'absent' ? `${b.name} · nghỉ` : b.name;
        ctx.font = font(26, 600);
        ctx.fillText(fit(ctx, [name, b.note, b.location].filter(Boolean).join(' · '), width - 2 * m - 300), m + 280, y + 56);
        y += rowH;
      });
    }
    y += 48;
  });

  // The free-time list shows only free windows, so it needs no key.
  if (opts.mode !== 'free') legend(ctx, m, height - m - 40, opts.mode, 24);
  ctx.font = font(22);
  ctx.fillStyle = C.muted;
  ctx.textAlign = 'right';
  ctx.fillText('Tạo bằng Macro Mate', width - m, height - m);
  ctx.textAlign = 'left';
}

// Draws the picture on `canvas` (made when omitted) and returns it.
export async function drawSchedule({ canvas = document.createElement('canvas'), week, appointments, mode = 'all', personKey, personName,
  hideNames = false, orientation = 'landscape', coachName = '' }) {
  try {
    await document.fonts?.load(font(20, 700));
    await document.fonts?.load(font(20));
  } catch {
    // The system font stands in.
  }
  const items = blocks(week, appointments || [], { mode, personKey, hideNames });
  const opts = { mode, personName, coachName };
  if (orientation === 'portrait') portrait(canvas, week, items, opts);
  else landscape(canvas, week, items, opts);
  return canvas;
}

export const fileName = (week, mode) => `${mode === 'free' ? 'lich-trong' : 'lich-tap'}-${dm(week).replace('/', '-')}-${dm(addDays(week, 6)).replace('/', '-')}.png`;
